import React, { useState, useEffect, useRef } from 'react';
import { TextField, Grid, FormControl, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FormContainer from '../../components/FormContainer';
import { addDistrict, updateDistrict, fetchStates } from '../../utils/api';

const DistrictForm = ({ editDistrict = null, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [states, setStates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const isEditMode = !!editDistrict;
  const onSuccessTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (onSuccessTimerRef.current != null) {
        clearTimeout(onSuccessTimerRef.current);
        onSuccessTimerRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    const loadStates = async () => {
      try {
        const response = await fetchStates();
        if (cancelled) return;
        setStates(response.data || []);
      } catch (error) {
        console.error('Error fetching states:', error);
        if (!cancelled) setErrorMessage('Failed to load states. Please refresh the page.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadStates();
    return () => {
      cancelled = true;
    };
  }, []);

  const validationSchema = Yup.object({
    districtName: Yup.string()
      .required('District name is required')
      .max(100, 'District name must be at most 100 characters')
      .matches(/^[a-zA-Z\s]+$/, 'District name can only contain letters and spaces'),
    stateId: Yup.number().required('State is required'),
  });

  const formik = useFormik({
    initialValues: {
      districtName: editDistrict ? editDistrict.districtName : '',
      stateId: editDistrict ? editDistrict.state?.stateId : '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      setSuccessMessage(null);
      setErrorMessage(null);

      try {
        const districtData = {
          districtName: values.districtName,
          state: {
            stateId: values.stateId
          }
        };
        
        if (isEditMode) {
          await updateDistrict(editDistrict.districtId, districtData, {
            previousData: editDistrict,
            identifier: editDistrict.districtName || values.districtName
          });
          setSuccessMessage('District updated successfully!');
        } else {
          await addDistrict(districtData, {
            identifier: values.districtName
          });
          setSuccessMessage('District added successfully!');
          resetForm();
        }

        if (onSuccess) {
          if (onSuccessTimerRef.current != null) clearTimeout(onSuccessTimerRef.current);
          onSuccessTimerRef.current = setTimeout(() => {
            onSuccessTimerRef.current = null;
            onSuccess();
          }, 1500);
        }
      } catch (error) {
        console.error(`Error ${isEditMode ? 'updating' : 'adding'} district:`, error);
        setErrorMessage(error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'add'} district. Please try again.`);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  if (isLoading) {
    return <div>Loading states...</div>;
  }

  return (
    <FormContainer
      title={isEditMode ? "Edit District" : "Add District"}
      onSubmit={formik.handleSubmit}
      isSubmitting={isSubmitting}
      successMessage={successMessage}
      errorMessage={errorMessage}
      onCloseSnackbar={handleCloseSnackbar}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl 
            fullWidth 
            margin="normal"
            error={formik.touched.stateId && Boolean(formik.errors.stateId)}
          >
            <InputLabel id="state-select-label">State</InputLabel>
            <Select
              labelId="state-select-label"
              id="stateId"
              name="stateId"
              value={formik.values.stateId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="State"
            >
              {states.map((state) => (
                <MenuItem key={state.stateId} value={state.stateId}>
                  {state.stateName}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.stateId && formik.errors.stateId && (
              <FormHelperText>{formik.errors.stateId}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="districtName"
            name="districtName"
            label="District Name"
            value={formik.values.districtName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.districtName && Boolean(formik.errors.districtName)}
            helperText={formik.touched.districtName && formik.errors.districtName}
            margin="normal"
            variant="outlined"
          />
        </Grid>
      </Grid>
    </FormContainer>
  );
};

export default DistrictForm; 